import { useState, useEffect } from "react";
import { BASE_URL, getFetchHttpOptions } from "src/util/appConfig";

import { isOnGenericUrl } from "src/util/url";
import { saveItem } from "src/services/sessionStorage";
import {
  HOME_REALM_SESSION_KEY,
  hasUsableHomeRealmConfig,
  readCachedHomeRealmConfig,
  shouldFetchHomeRealmDiscovery,
} from "src/util/homeRealmDiscoveryConfig";
import {
  getHomeRealmDiscoveryPrefetch,
  startHomeRealmDiscoveryPrefetch,
} from "src/util/prefetchHomeRealmDiscovery";
import {
  logBootstrapSummary,
  markBootstrap,
  measureBootstrap,
} from "src/util/bootstrapTiming";

const resolveInitialFetching = () => {
  if (isOnGenericUrl()) {
    return false;
  }
  return !readCachedHomeRealmConfig();
};

const useFetchConfig = () => {
  const [config, setConfig] = useState(() => readCachedHomeRealmConfig());
  const [isFetchingConfig, setIsFetchingConfig] = useState(
    resolveInitialFetching
  );

  useEffect(() => {
    const fetchData = async () => {
      if (isOnGenericUrl()) {
        setIsFetchingConfig(false);
        return;
      }

      if (hasUsableHomeRealmConfig(config)) {
        setIsFetchingConfig(false);
        return;
      }

      markBootstrap("config-start");

      try {
        let response = null;
        const prefetched = getHomeRealmDiscoveryPrefetch();
        if (prefetched) {
          response = await prefetched;
        }

        if (!hasUsableHomeRealmConfig(response)) {
          response = await fetch(
            `${BASE_URL}/functions/home-realm-discovery`,
            getFetchHttpOptions()
          ).then((res) => res.json());
        }

        if (hasUsableHomeRealmConfig(response)) {
          setConfig(response);
          saveItem(HOME_REALM_SESSION_KEY, response);
        }
      } catch (error) {
        // fall through — Auth0 falls back to REACT_APP_AUTH0_* env vars
      } finally {
        markBootstrap("config-end");
        measureBootstrap("config-fetch", "config-start", "config-end");
        setIsFetchingConfig(false);
        logBootstrapSummary();
      }
    };

    if (shouldFetchHomeRealmDiscovery() && !getHomeRealmDiscoveryPrefetch()) {
      startHomeRealmDiscoveryPrefetch();
    }

    fetchData();
  }, [config]);

  return {
    config,
    isGenericDomain: !!config?.auth0Connection,
    isFetchingConfig,
  };
};

export default useFetchConfig;
